import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from './ThemeProvider';
import { PopoupProps } from 'tmwx-design-common';
import './css/Menu.css';

const Popup: React.FC<PopoupProps> = ({
    children,
    triggerComponent,
    type = 'click',
    closeAfterClick = false,
    style
}) => {
    const { roundness } = useTheme();
    const [isShown, setIsShown] = useState(false);
    const popupRef = useRef<HTMLDivElement>(null);
    const popupContentRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    const handleClickOutside = (event: MouseEvent) => {
        if (!isShown) return;

        const clickedInsidePopup = popupContentRef.current?.contains(event.target as Node);
        const clickedOnTrigger = triggerRef.current?.contains(event.target as Node);

        if (closeAfterClick || (!clickedInsidePopup && !clickedOnTrigger)) {
            setIsShown(false);
        }
    };

    const handleToggleButtonClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation(); // Fontos! Megakadályozza, hogy azonnal bezáródjon a popup

        if (isShown) {
            setIsShown(false);
        } else {
            const triggerRect = triggerRef.current?.getBoundingClientRect();
            if (!triggerRect) return;

            const popupWidth = popupContentRef.current?.offsetWidth || 0;
            const popupHeight = popupContentRef.current?.offsetHeight || 0;
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;

            let calculatedLeft = triggerRect.left + window.scrollX;
            let calculatedTop = triggerRect.bottom + window.scrollY;

            if (calculatedLeft + popupWidth > windowWidth) {
                calculatedLeft = windowWidth - popupWidth;
            }
            if (calculatedTop + popupHeight > windowHeight) {
                calculatedTop = triggerRect.top + window.scrollY - popupHeight;
            }

            setPosition({ top: calculatedTop, left: calculatedLeft });
            setIsShown(true);
        }
    };

    useEffect(() => {
        if (isShown) {
            setTimeout(() => {
                document.addEventListener("click", handleClickOutside);
            }, 0); // Késleltetés, hogy ne záródjon be azonnal a megnyitás után
        }

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [isShown]);

    const popupContent = (
        <div
            className={`popup-menu ${isShown ? 'shown' : ''}`}
            style={{
                position: type === 'click' ? 'fixed' : 'absolute',
                top: `${position.top}px`,
                left: `${position.left}px`,
                transformOrigin: 'left top',
            }}
            ref={popupContentRef}
        >
            {children}
        </div>
    );

    return (
        <div className="popup-menu-container" ref={popupRef} style={{ display: 'flex', ...style }}>
            <div>
                <div
                    ref={triggerRef}
                    className='ripple'
                    style={{ borderRadius: roundness, cursor: 'pointer' }}
                    onClick={type === 'click' ? handleToggleButtonClick : undefined}
                    onMouseEnter={type === 'hover' ? handleToggleButtonClick : undefined}
                    onMouseLeave={type === 'hover' ? () => setIsShown(false) : undefined}
                >
                    {triggerComponent}
                </div>
                {createPortal(popupContent, document.body)}
            </div>
        </div>
    );
};

export default Popup;